import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2, Copy, Check, Share2, ArrowLeft,
  Shield, Swords, Scale, FileText, AlertTriangle,
  Target, CheckCircle, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_COLORS = {
  athena: 'text-amber-400', hermes: 'text-orange-400', apollo: 'text-yellow-400',
  eris: 'text-red-400', themis: 'text-blue-400', calliope: 'text-pink-400',
  hestia: 'text-orange-300', plutus: 'text-green-400', nike: 'text-yellow-400', metis: 'text-indigo-400',
};

const CONSENSUS_STYLES = {
  strong: { label: 'Strong Consensus', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  moderate: { label: 'Moderate Consensus', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  divided: { label: 'Council Divided', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
};

const WarRoomSharePage = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/war-council/warroom/shared/${shareId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || 'Brief not found');
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-red-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading War Room Brief...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-4xl mb-3">⚔️</p>
          <h1 className="text-xl font-bold text-white mb-2">Brief Not Found</h1>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <Link to="/" className="text-red-400 hover:text-red-300 text-sm flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Go to iSupplyAI
          </Link>
        </div>
      </div>
    );
  }

  const verdict = data?.verdict;
  const consensusStyle = CONSENSUS_STYLES[verdict?.consensus_level] || CONSENSUS_STYLES.moderate;

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="warroom-share-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-gray-950/90 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="text-red-400 font-bold text-sm">iSupplyAI</span>
            <span className="text-gray-600 text-xs">War Room</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{data?.views || 0} views</span>
            <button
              onClick={copyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-full text-xs text-gray-300 transition-colors"
              data-testid="copy-share-link"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Hero */}
        <div className="text-center space-y-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
            {data?.brand_name} — Strategic War Room Brief
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
            {data?.topic}
          </h1>
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <span>{data?.responding_gods?.length || 0} gods debated</span>
            <span>-</span>
            <span>{new Date(data?.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Phase 1: Briefing */}
        {data?.briefing?.length > 0 && (
          <Section icon={<Shield className="w-4 h-4 text-blue-400" />} title="Intelligence Briefing" phase="1">
            <div className="space-y-2">
              {data.briefing.map((b) => (
                <GodMessage key={b.god_id} god={b} />
              ))}
            </div>
          </Section>
        )}

        {/* Phase 2: Positions */}
        {data?.positions?.length > 0 && (
          <Section icon={<Swords className="w-4 h-4 text-amber-400" />} title="Opening Positions" phase="2">
            <div className="grid gap-3 sm:grid-cols-2">
              {data.positions.map((pos) => (
                <PositionCard key={pos.god_id} position={pos} />
              ))}
            </div>
          </Section>
        )}

        {/* Phase 3: Clash */}
        {data?.clash_rounds?.length > 0 && (
          <Section icon={<Scale className="w-4 h-4 text-red-400" />} title="The Clash" phase="3">
            {data.clash_rounds.map((round, roundIdx) => (
              <div key={roundIdx} className="space-y-2 mb-4">
                <p className="text-[10px] text-red-400/50 uppercase tracking-wider font-semibold">
                  Round {roundIdx + 1}
                </p>
                {round.map((c, idx) => (
                  <GodMessage key={`${roundIdx}-${idx}`} god={c} showChallenge />
                ))}
              </div>
            ))}
          </Section>
        )}

        {/* Phase 4: Verdict */}
        {verdict && (
          <Section icon={<FileText className="w-4 h-4 text-green-400" />} title="The Verdict" phase="4">
            {/* Consensus & Summary */}
            <div className={`${consensusStyle.bg} border ${consensusStyle.border} rounded-xl p-4 mb-4`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className={`w-5 h-5 ${consensusStyle.color}`} />
                <span className={`text-sm font-bold ${consensusStyle.color}`}>{consensusStyle.label}</span>
              </div>
              <p className="text-gray-200 text-sm leading-relaxed">{verdict.summary}</p>
            </div>

            {/* Recommendations */}
            {verdict.recommendations?.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider flex items-center gap-2">
                  <Target className="w-3.5 h-3.5" /> Priority Actions
                </p>
                {verdict.recommendations.map((rec, i) => (
                  <div key={i} className="bg-gray-800/30 border border-gray-700/40 rounded-xl p-3 flex gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      i === 0 ? 'bg-green-500/20 text-green-400' : i === 1 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700/50 text-gray-400'
                    }`}>
                      <span className="text-xs font-bold">P{rec.priority || i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-200 font-medium">{rec.action}</p>
                      <p className="text-xs text-gray-500 mt-1">{rec.rationale}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {rec.owner_emoji && (
                          <span className="text-xs text-gray-500">
                            {rec.owner_emoji} <span className={GOD_COLORS[rec.owner_god] || 'text-gray-400'}>{rec.owner_name}</span>
                          </span>
                        )}
                        {rec.metric_to_track && (
                          <span className="text-[10px] px-2 py-0.5 bg-gray-700/50 text-gray-400 rounded-full">
                            Track: {rec.metric_to_track}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Risks */}
            {verdict.risks?.length > 0 && (
              <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3 mb-4">
                <p className="text-xs text-orange-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Key Risks
                </p>
                <ul className="space-y-1">
                  {verdict.risks.map((risk, i) => (
                    <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                      <span className="text-orange-500/60 mt-0.5">-</span> {risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Dissenting Opinion */}
            {verdict.dissenting_opinion && (
              <div className="bg-red-900/10 border border-red-500/20 rounded-xl p-3">
                <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">
                  Devil's Advocate — Unresolved Concern
                </p>
                <p className="text-xs text-gray-400 leading-relaxed italic">"{verdict.dissenting_opinion}"</p>
              </div>
            )}
          </Section>
        )}

        {/* CTA Footer */}
        <div className="text-center py-8 border-t border-gray-800/50">
          <p className="text-gray-500 text-xs mb-3">
            Powered by <span className="text-red-400 font-semibold">iSupplyAI</span> War Room
          </p>
          <p className="text-gray-600 text-xs mb-4">
            AI gods that argue strategy so you don't have to guess.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-red-500/20"
            data-testid="share-cta-btn"
          >
            Try iSupplyAI Free <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════

const Section = ({ icon, title, phase, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
        <span className="text-[10px] text-gray-400 font-bold">{phase}</span>
      </div>
      {icon}
      <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider">{title}</h2>
    </div>
    {children}
  </div>
);

const GodMessage = ({ god, showChallenge = false }) => {
  const isDevil = god.role === 'devil_advocate';
  const textColor = GOD_COLORS[god.god_id] || 'text-gray-400';

  return (
    <div className={`flex gap-3 p-3 rounded-xl ${
      isDevil ? 'bg-red-900/10 border border-red-500/20' : 'bg-gray-800/20 border border-gray-700/30'
    }`}>
      <span className="text-lg flex-shrink-0">{god.god_emoji}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`font-bold text-sm ${textColor}`}>{god.god_name}</span>
          {isDevil && (
            <span className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-semibold">
              DEVIL'S ADVOCATE
            </span>
          )}
          {showChallenge && god.challenges && (
            <span className="text-[10px] text-gray-500">
              challenges <span className={GOD_COLORS[god.challenges] || 'text-gray-400'}>{god.challenges}</span>
            </span>
          )}
        </div>
        <p className="text-sm text-gray-300 leading-relaxed">{god.content}</p>
      </div>
    </div>
  );
};

const PositionCard = ({ position }) => {
  const isDevil = position.role === 'devil_advocate';
  const textColor = GOD_COLORS[position.god_id] || 'text-gray-400';

  return (
    <div className={`rounded-xl border p-3 ${
      isDevil ? 'bg-red-900/10 border-red-500/30' : 'bg-gray-800/20 border-gray-700/40'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{position.god_emoji}</span>
        <span className={`font-bold text-sm ${isDevil ? 'text-red-400' : textColor}`}>{position.god_name}</span>
        {isDevil && (
          <span className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-semibold">DA</span>
        )}
      </div>
      <div className="mb-2">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Thesis</p>
        <p className="text-sm text-gray-200 leading-relaxed">{position.thesis}</p>
      </div>
      {position.evidence && (
        <div className="mb-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Evidence</p>
          <p className="text-xs text-gray-400 leading-relaxed">{position.evidence}</p>
        </div>
      )}
      {position.risk && (
        <div className="bg-black/20 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wider text-orange-500/80 font-semibold mb-0.5">Risk</p>
          <p className="text-xs text-gray-400 leading-relaxed">{position.risk}</p>
        </div>
      )}
    </div>
  );
};

export default WarRoomSharePage;
